import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { productHooks } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';

const schema = z.object({
  species: z.string().min(1, 'Required'),
});

export default function ProductsPage() {
  const { data: products = [], isLoading } = productHooks.useList({ includeInactive: 'true' });
  const createMut = productHooks.useCreate();
  const updateMut = productHooks.useUpdate();
  const deleteMut = productHooks.useDelete();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  const openCreate = () => { setEditing(null); reset({}); setModalOpen(true); };
  const openEdit = (p) => { setEditing(p); reset({ species: p.species }); setModalOpen(true); };
  const onSubmit = async (values) => {
    if (editing) await updateMut.mutateAsync({ id: editing.id, ...values });
    else await createMut.mutateAsync(values);
    setModalOpen(false); reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Product / Species Master</h2>
          <p className="text-sm text-gray-500">Manage species names. Size, form, and packing are entered per gate pass.</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add Species</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Species / Product', 'Status', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{p.species}</td>
                    <td className="px-4 py-3">
                      <span className={"badge " + (p.isActive ? 'badge-approved' : 'badge-cancelled')}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => openEdit(p)} className="text-gray-400 hover:text-blue-600"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteTarget(p)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Species' : 'Add Species'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Species / Product Name" required placeholder="e.g. Vannamei Shrimp, Black Tiger Prawn" error={errors.species?.message} {...register('species')} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>{editing ? 'Save Changes' : 'Add Species'}</Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={async () => { await deleteMut.mutateAsync(deleteTarget.id); setDeleteTarget(null); }}
        loading={deleteMut.isPending} title="Delete Species"
        message={`Delete "${deleteTarget?.species}"?`} confirmLabel="Delete"
      />
    </div>
  );
}
